import {ErrorMessage, Formik} from 'formik';
import React, {useState} from 'react';
import {CSSTransition} from 'react-transition-group';
import BasicButton from '../../../../components/BasicButton';
import BasicButtonGroup from '../../../../components/BasicButtonGroup';
import BasicLabel from '../../../../components/BasicLabel';
import BasicSelect from '../../../../components/BasicSelect';
import BasicSubmitButton from '../../../../components/BasicSubmitButton';
import CheckboxMultiSelect from '../../../../components/CheckboxMultiSelect';
import CheckboxOrRadioGroup from '../../../../components/CheckboxOrRadioGroup';
import DatePicker from '../../../../components/DatePicker';
import LoadMask from '../../../../components/LoadMask';
import {ANIMATION_TIMEOUT_APPEAR} from '../../../../constants/animation';
import ENDPOINT from '../../../../constants/endpoints';
import {DateHelpers} from '../../../../helpers/date-helpers';
import useApi from '../../../../hooks/useApi';
import {MassSendJobFormValidationSchema} from './validate';

const MassSendJobForm = props => {
  const {
    params: { ids, successBtnText = 'Apply Candidate' },
    actions: { handleError, proceed, dismiss },
  } = props;

  const api = useApi();

  const [state, setState] = useState({
    clientTitle: 'Client',
    fields: {
      searchType: null,
      clients: null,
      candidates: ids,
      status: null,
      processes: null,
      dateApply: new Date(),
      IgnoreOffJobNotification: 'No',
    },
    options: { processes: [], status: [], clients: [] },
  });

  const getClientOptions = async assigned =>
    new Promise(resolve => {
      api.get(
        ENDPOINT.FORM.OPTIONS.MASS_SEND_JOB.CLIENTS,
        { assigned },
        {
          handleSuccess: data => resolve(data),
        }
      );
    });

  const getPositionOptions = async (assigned, firms) =>
    new Promise(resolve => {
      api.get(
        ENDPOINT.FORM.OPTIONS.MASS_SEND_JOB.POSITIONS,
        {
          firms: firms.map(firm => firm.value),
          assigned,
        },
        {
          handleSuccess: data => resolve(data),
        }
      );
    });

  const handleChangeSearchType = async (
    field,
    value,
    { setFieldValue, setSubmitting }
  ) => {
    setSubmitting(true);
    const options = await getClientOptions(value.assigned);
    setState({
      ...state,
      clientTitle: Number(value.assigned) ? 'Assigned Client' : 'Client',
      options: { ...state.options, clients: options, processes: [] },
    });
    setSubmitting(false);
    setFieldValue('clients', null);
    setFieldValue('processes', null);
    setFieldValue(field, value);
  };

  const handleChangeClients = async (
    field,
    value,
    { values, setFieldValue, setSubmitting }
  ) => {
    if (value) {
      setSubmitting(true);
      const options = await getPositionOptions(
        values.searchType.assigned,
        value
      );
      setState({
        ...state,
        options: { ...state.options, processes: options },
      });
      setSubmitting(false);
    }
    setFieldValue('processes', null);
    setFieldValue(field, value);
  };

  const handleSubmit = (
    values,
    { setSubmitting, setErrors, setFieldError }
  ) => {
    const params = {
      assigned: values.searchType.assigned,
      p: values.processes.map(p => p.value).join(','),
      c: ids.join(','),
      ignoreOffJM: values.IgnoreOffJobNotification,
    };

    if (values.searchType.assigned === 1 && values.dateApply) {
      params.dateApply = DateHelpers.jsDateToString(new Date(values.dateApply));
    }

    api.post(ENDPOINT.FORM.SAVE.MASS_SEND_JOB, params, {
      handleError: response => {
        setSubmitting(false);
        handleError(response, setErrors);
      },
      handleErrorCode: response => {
        setSubmitting(false);
        setFieldError('reason', response.data.message);
      },
      handleSuccess: () => {
        setSubmitting(false);
        proceed();
      },
    });
  };

  return (
    <CSSTransition
      in={true}
      appear={true}
      timeout={ANIMATION_TIMEOUT_APPEAR}
      classNames="fade"
    >
      <div className="form__inner">
        <Formik
          enableReinitialize={true}
          initialValues={state.fields}
          onSubmit={handleSubmit}
          validationSchema={MassSendJobFormValidationSchema}
        >
          {({
            values,
            errors,
            touched,
            handleChange,
            handleSubmit,
            isSubmitting,
            setSubmitting,
            setFieldValue,
            setFieldTouched,
          }) => (
            <form
              onSubmit={handleSubmit}
              className="form__content"
              style={{ maxWidth: '100%' }}
            >
              {isSubmitting && <LoadMask />}

              <div
                className="form__group form__group--compact"
                style={{ marginBottom: '15px' }}
              >
                <BasicLabel
                  name="searchTypeLabel"
                  required={true}
                  text="Search Type"
                  styles={{
                    fontWeight: 'bold',
                  }}
                />
                <BasicSelect
                  name="searchType"
                  value={values.searchType}
                  isValid={
                    (!errors.searchType && !!touched.searchType) ||
                    !touched.searchType
                  }
                  placeholder="Select From List"
                  options={[
                    { value: 1, label: 'GoBuyside', assigned: 1 },
                    { value: 2, label: 'Subscription', assigned: 0 },
                  ]}
                  onChange={(field, value) =>
                    handleChangeSearchType(field, value, {
                      setFieldValue,
                      setSubmitting,
                    })
                  }
                  onBlur={setFieldTouched}
                />

                <ErrorMessage
                  name="searchType"
                  render={message => (
                    <div className="form__error">{message}</div>
                  )}
                />
              </div>
              <div
                className="form__group form__group--compact"
                style={{ marginBottom: '15px' }}
              >
                <CheckboxMultiSelect
                  disabled={!values.searchType}
                  name="clients"
                  label={state.clientTitle}
                  labelStyles={{
                    fontWeight: 'bold',
                  }}
                  isValid={
                    (!errors.clients && !!touched.clients) || !touched.clients
                  }
                  required={true}
                  selectedItems={values.clients}
                  items={state.options.clients}
                  placeholder=""
                  groups={false}
                  onChange={(fields, value) =>
                    handleChangeClients(fields, value, {
                      values,
                      setFieldValue,
                      setSubmitting,
                    })
                  }
                  onBlur={setFieldTouched}
                />
                <ErrorMessage
                  name="clients"
                  render={message => (
                    <div className="form__error">{message}</div>
                  )}
                />
              </div>

              <div
                className="form__group form__group--compact"
                style={{ marginBottom: '30px' }}
              >
                <CheckboxMultiSelect
                  disabled={!values.clients}
                  name="processes"
                  label="Position"
                  labelStyles={{
                    fontWeight: 'bold',
                  }}
                  isValid={
                    (!errors.processes && !!touched.processes) ||
                    !touched.processes
                  }
                  required={true}
                  selectedItems={values.processes}
                  items={state.options.processes}
                  placeholder=""
                  groups={true}
                  onChange={setFieldValue}
                  onBlur={setFieldTouched}
                />

                <ErrorMessage
                  name="processes"
                  render={message => (
                    <div className="form__error">{message}</div>
                  )}
                />
              </div>

              {values.searchType && values.searchType.value === 1 && (
                <div
                  className="form__group form__group--compact"
                  style={{ marginBottom: '20px' }}
                >
                  <div
                    className="form__row form__row--compact"
                    style={{ justifyContent: 'space-between' }}
                  >
                    <div className="form__column">
                      <BasicLabel
                        name="dateApply"
                        required={true}
                        text="Apply Date"
                        styles={{
                          fontWeight: 'bold',
                        }}
                      />
                    </div>

                    <div className="form__column">
                      <DatePicker
                        name="dateApply"
                        onChange={setFieldValue}
                        dateFormat="Y-MM-dd HH:mm"
                        selected={values.dateApply}
                        showTime={true}
                        maxDate={new Date()}
                        icon={true}
                        styles={{
                          maxWidth: '100%',
                          justifyContent: 'flex-end',
                        }}
                      />
                    </div>
                  </div>

                  <ErrorMessage
                    name="dateApply"
                    render={message => (
                      <div className="form__error">{message}</div>
                    )}
                  />
                </div>
              )}

              <div
                className="form__group form__group--compact"
                style={{ marginBottom: 0 }}
              >
                <div className="form__row form__row--compact">
                  <div className="form__column">
                    <BasicLabel
                      name="IgnoreOffJobNotification"
                      text={`Send Job to candidates with email preferences of turn off job notifications?`}
                    />
                  </div>
                </div>
              </div>

              <div
                className="form__group form__group--compact"
                style={{ marginBottom: '15px' }}
              >
                <div className="form__row form__row--compact">
                  <div className="form__column">
                    <CheckboxOrRadioGroup
                      name="IgnoreOffJobNotification"
                      type="radio"
                      options={['Yes', 'No']}
                      selectedOptions={values.IgnoreOffJobNotification}
                      directionRow={true}
                      controlFunc={handleChange}
                    />
                  </div>
                </div>
              </div>
              {values.searchType && (
                <div>
                  <b>
                    {values.searchType.assigned
                      ? 'When you click on Send button these Candidates will be automatically applied to the job and receive Setup Email.'
                      : 'When you click on Send button these Candidates will receive an email with a suggestion to apply for this job.'}
                  </b>
                </div>
              )}

              {errors.reason && (
                <div
                  className="form__group form__group--compact"
                  style={{ marginBottom: '15px' }}
                >
                  <div className="form__error">{errors.reason}</div>
                </div>
              )}

              <div className="form__controls form__controls--compact">
                <BasicButtonGroup groupStyles={{ width: '100%' }}>
                  <BasicSubmitButton
                    controlFunc={handleSubmit}
                    btnWidth={200}
                    btnHeight={40}
                    btnFontWeight={500}
                    btnArrow={true}
                    btnTextColor="#27282d"
                    btnText={successBtnText}
                  />

                  <BasicButton
                    controlFunc={dismiss}
                    btnWidth={200}
                    btnHeight={40}
                    btnFontWeight={500}
                    btnArrow={false}
                    btnTextColor="#27282d"
                    btnText="Cancel"
                  />
                </BasicButtonGroup>
              </div>
            </form>
          )}
        </Formik>
      </div>
    </CSSTransition>
  );
};

export default MassSendJobForm;
